/**
 * The footer on every page: what Morcast is, links for brands and creators, the docs with the
 * roadmap, the public protocol anyone can use to check Morcast's results, the support address,
 * the privacy policy and Morcast's account on X, shown as X's icon at the end of the last row.
 */

import { Link } from "react-router";

import { useAppConfig } from "../config.js";
import { Logo } from "./Logo.js";
import { styles } from "./ui.js";

/** The public protocol, which anyone can use to check Morcast's results. */
const PROTOCOL_URL = "https://github.com/Morphora-Lab/morcast-protocol";

/** Morcast's own account on X, where people follow and contact the team. */
export const X_ACCOUNT = { handle: "@morcastnet", url: "https://x.com/morcastnet" } as const;

/** X's logo, drawn in the text's colour. */
function XLogo({ className }: { className: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/** A link in the footer's small print. */
const smallLink =
  "text-zinc-600 underline decoration-zinc-900/20 underline-offset-4 transition-colors hover:text-zinc-950 dark:text-zinc-300 dark:decoration-white/25 dark:hover:text-white";

const COLUMNS: { title: string; links: { label: string; to?: string; href?: string }[] }[] = [
  {
    title: "Brands",
    links: [
      { label: "Brand guide", to: "/brands" },
      { label: "Start a campaign", to: "/campaigns/new" },
      { label: "Campaign requests", to: "/campaigns/requests" },
    ],
  },
  {
    title: "Creators",
    links: [
      { label: "Creator guide", to: "/creators" },
      { label: "Browse campaigns", to: "/campaigns" },
      { label: "Linked accounts", to: "/accounts" },
      { label: "Payouts", to: "/payouts" },
    ],
  },
  {
    title: "Morcast",
    links: [
      { label: "Docs", to: "/docs" },
      { label: "Roadmap", to: "/docs/roadmap" },
      {
        label: "Morcast Payment Protocol",
        href: PROTOCOL_URL,
      },
    ],
  },
];

export function SiteFooter() {
  const linkClass =
    "text-sm text-zinc-600 transition-colors hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white";
  const chip =
    "inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm ring-1 ring-zinc-900/[0.08] ring-inset dark:bg-white/[0.04] dark:shadow-none dark:ring-white/10";
  return (
    <footer className="relative isolate overflow-hidden border-t border-zinc-900/[0.07] bg-white text-zinc-700 dark:border-white/[0.07] dark:bg-ink-950 dark:text-zinc-300">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-48 left-1/2 -z-10 h-96 w-[64rem] max-w-[140vw] -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-300/15 via-brand-200/10 to-brand-400/15 blur-3xl dark:from-white/[0.03] dark:via-white/[0.015] dark:to-white/[0.03]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/25 to-transparent dark:via-white/10"
      />
      <div className={`grid gap-12 py-16 sm:grid-cols-2 sm:py-20 lg:grid-cols-5 ${styles.frame}`}>
        <div className="lg:col-span-2">
          <Logo />
          <p className="mt-5 max-w-sm text-sm leading-6 text-zinc-600 dark:text-zinc-400">
            Morcast runs pay-for-performance creator campaigns on Base. Brands pay only for verified
            organic reach, and creators claim their earnings from the escrow.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
            <li className={chip}>
              <span aria-hidden className="size-2 rounded-full bg-[#0052ff]" />
              Settled on Base
            </li>
            <li className={chip}>
              <span
                aria-hidden
                className="size-2 rounded-full bg-gradient-to-br from-brand-400 to-brand-600"
              />
              Open-source protocol
            </li>
          </ul>
        </div>
        {COLUMNS.map((column) => (
          <nav key={column.title} aria-label={column.title}>
            <h2 className="text-xs font-semibold tracking-[0.12em] text-zinc-500 uppercase dark:text-zinc-300">
              {column.title}
            </h2>
            <ul className="mt-5 space-y-3">
              {column.links.map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} rel="noopener" className={linkClass}>
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <div className="border-t border-zinc-900/[0.07] dark:border-white/[0.07]">
        <div
          className={`flex flex-col gap-3 py-6 text-xs text-zinc-500 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8 dark:text-zinc-400 ${styles.frame}`}
        >
          <p>
            Every result is published with its evidence, and anyone can check it with the{" "}
            <a href={PROTOCOL_URL} rel="noopener" className={smallLink}>
              open-source Morcast Payment Protocol
            </a>
            .
            <Contact />
          </p>
          <div className="flex shrink-0 items-center gap-4">
            <Link to="/privacy" className={smallLink}>
              Privacy policy
            </Link>
            <span aria-hidden className="h-4 w-px bg-zinc-900/10 dark:bg-white/15" />
            <a
              href={X_ACCOUNT.url}
              rel="me noopener"
              aria-label="Morcast on X"
              title="Morcast on X"
              className="-m-1.5 rounded-full p-1.5 text-zinc-500 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-zinc-400 dark:hover:bg-white/[0.07] dark:hover:text-white"
            >
              <XLogo className="size-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Where people write to Morcast, once the API publishes an address. */
function Contact() {
  const { supportEmail } = useAppConfig();
  if (!supportEmail) {
    return null;
  }
  return (
    <>
      {" "}
      Questions? Write to{" "}
      <a href={`mailto:${supportEmail}`} className={smallLink}>
        {supportEmail}
      </a>
      .
    </>
  );
}
